import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { planService } from '../../application/PlanService';
import { PlanActionType } from '../../context/plan/actions';
import { usePlanContext } from '../../context/plan/context';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UseRetrieveActivePlanResponse = ServiceResponse<() => void, PlanFlat>;

// Hooks act as 'Adapter' layer.
export const useRetrieveActivePlan = (): UseRetrieveActivePlanResponse => {
  const { data: project } = useRetrieveActiveProject();
  const { planId } = useParams();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { planState, planDispatch } = usePlanContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { isLoading, isError, refetch, isSuccess } = useQuery(
    [`planner_active_plan`, planId],
    () => planService.fetch({ planId: planId!, project: project! }),
    {
      staleTime: Infinity,
      enabled: planId !== undefined && project !== undefined,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (activePlan) => {
        planDispatch({
          type: PlanActionType.SET_ACTIVE_PLAN,
          payload: {
            activePlan,
          },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve Active Plan Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `plan_not_found`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: planState.activePlan.plan,
  };
};
